import React, { useEffect, useMemo, useState } from 'react';
import { Button, ButtonType, ConfirmModal, Input } from '@bettercomp/ui-components';
import { Col, Divider, Row } from 'antd';
import cn from './styles.module.scss';
import { AuthErrors, AuthForm } from '../common/AuthForm';
import { showNotification } from 'common/function';
import {
  CreateHrisIntegrationRequest,
  HRISAuth,
  HRISAuthProvider,
  HRISConfigName,
  HRISIntegration,
  HrisIntegrationService,
  HRISIntegrationType,
} from 'types';
import { isScheduleValid, isUrlValid, ScheduleControl } from 'components/UserSettings/HRIS/common/ScheduleControl';
import { reportError } from 'utils/reporting';
import { Loader } from 'components/Loader';
import { TimezoneSelector } from 'components/UserSettings/HRIS/common/TimezoneSelector';
import { isApiError } from 'common/types';
import { SectionHeader } from 'components/UserSettings/common/SectionHeader';

type Errors = {
  schedule?: string,
  timezone?: string,
} & AuthErrors

function isValidForm(form: Partial<HRISIntegration>): form is CreateHrisIntegrationRequest {
  return !!form.auth && !!form.schedule && !!form.timezone;
}

const validateForm = (form: Partial<HRISIntegration>): Errors => {
  const errors: Errors = {};
  if (!form.schedule) {
    errors.schedule = 'Required';
  }
  if (form.schedule && !isScheduleValid(form.schedule)) {
    errors.schedule = 'All fields are required';
  }
  if (!form.timezone) {
    errors.timezone = 'Required';
  }
  if (!form.auth?.config?.url) {
    errors.url = 'Required';
  }

  if (form.type === HRISIntegrationType.WORKDAY_RAAS && form.config?.url && !isUrlValid(form.config.url)) {
    errors.url = 'Url is not valid. format=json is allowed only';
  }

  if (!form.auth?.config?.client_id) {
    errors.client_id = 'Required';
  }

  if (!form.auth?.config?.client_secret) {
    errors.client_secret = 'Required';
  }

  if (!form.auth?.config?.grant_type) {
    errors.grant_type = 'Required';
  }

  if (!form.auth?.config?.refresh_token) {
    errors.refresh_token = 'Required';
  }

  return errors;
}

export function getConfigTestCopyText(configName: HRISConfigName): string {
  switch(configName){
    case HRISConfigName.JOBS_CONFIG:
      return 'Jobs report API endpoint'
    case HRISConfigName.EMPLOYEES_CONFIG:
      return 'Employees report API endpoint';
    case HRISConfigName.RANGES_CONFIG:
      return 'Ranges report API endpoint'
    default:
      return configName
  }
}

export const WoorkdayRaaS = () => {
  const [loadingTest, setLoadingTest] = useState(false);
  const [integration, setIntegration] = useState<Partial<HRISIntegration>>();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const loadIntegration = async () => {
    setLoading(true);
    try {
      const integration = await HrisIntegrationService.listHrisIntegrations({
        type: HRISIntegrationType.WORKDAY_RAAS,
        includeAuth: true,
      });
      setIntegration(integration[0]);
    } catch (err: any) {
      reportError(err);
      showNotification(isApiError(err) ? err.body.error : err.message, 'Load Workday RaaS integration error', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadIntegration();
  }, []);

  const testDisabled = useMemo(() => {
    const {client_id, client_secret, url, refresh_token, grant_type} = integration?.auth?.config || {};

    return !client_id || !client_secret || !url || !refresh_token || !grant_type;
  }, [integration?.auth]);

  const handleAuthChange = (auth: HRISAuth) => {
    setErrors({});
    setIntegration({
      ...integration,
      auth,
    });
  };

  const handleChange = (value: string, name: string) => {
    setErrors({});
    setIntegration({
      ...integration,
      [name]: value,
    })
  }

  const testFileEndpoint = async (configName: HRISConfigName) => {
    const url = integration?.[configName]?.url
    if (integration?.auth?.provider === HRISAuthProvider.OAUTH2 && url) {

      if (!isUrlValid(url)) {
        showNotification('Failure', 'Url is not valid. format=json is allowed only', 'error');
        return;
      }

      setLoadingTest(true);
      try {
        const response = await HrisIntegrationService.testHrisUrl({
          requestBody: {
            auth: integration.auth,
            url: integration[configName]?.url,
            configName,
          }
        });

        if (response.result === 'success') {
          showNotification(`${getConfigTestCopyText(configName)} test job started successfully. You will be notified when complete.`, 'HRIS Test Started', 'success');
        } else {
          const message = response.message || `Request failed with status code ${response.status}`;
          showNotification(message, 'HRIS Test Failed', 'error')
        }
      } catch (err: any) {
        reportError(err);
        showNotification(isApiError(err) ? (err.body.error || err.body.message) : err.message, 'Failed to test URL', 'error');
      }
      setLoadingTest(false);
    }
  }

  const handleSave = async () => {
    try {
      if (integration) {
        const newErrors = validateForm(integration);

        if (!!Object.values(newErrors).length) {
          setErrors(newErrors);
          return;
        }
        setSaveLoading(true);
        if (integration?.id) {
          const updatedIntegration = await HrisIntegrationService.updateHrisIntegration({
            id: integration.id,
            requestBody: integration,
          });
          setIntegration(updatedIntegration);
        } else {
          if (isValidForm(integration)) {
            const newIntegration = await HrisIntegrationService.createHrisIntegration({
              requestBody: {
                ...integration,
                type: HRISIntegrationType.WORKDAY_RAAS,
              }
            });
            setIntegration(newIntegration);
          }
        }
        showNotification('', 'Workday RaaS Settings have been saved', 'success');
      }
    } catch (err: any) {
      if (err.status !== 400) {
        reportError(err);
      }

      const message = err.body?.details
        ? err.body.details?.body?.map(({ message }: { message: string }) => <>{message};<br /></>)
        : err.body?.message;

      showNotification('Failed to save Workday RaaS integration', message, 'error');
    } finally {
      setSaveLoading(false);
    }
  }

  const handleDeactivate = async () => {
    try {
      setDeleteLoading(true);
      if (integration?.id) {
        await HrisIntegrationService.deleteHrisIntegration({
          id: integration.id,
        });
        setIntegration(undefined);
      }
    } catch (err: any) {
      reportError(err);
      showNotification(isApiError(err) ? err.body.error : err.message, 'Failed to save Workday RaaS integration', 'error');
    } finally {
      setDeleteLoading(false);
    }
  }

  const handleConfigChange = (configName: HRISConfigName, event: React.ChangeEvent<HTMLInputElement>) => {
    setIntegration({
      ...integration,
      [configName]: integration?.[configName] ? {
        ...integration[configName],
        [event.target.name]: event.target.value,
      } : {
        [event.target.name]: event.target.value,
      }
    })
  }

  return (
    <div className={cn.content}>
      {loading ? (
        <Loader/>
      ) : (
        <>
          <Row gutter={[12, 24]}>
            <Col xs={24}>
              <AuthForm
                form={integration?.auth}
                onChange={handleAuthChange}
                errors={errors}
              />
            </Col>
            <Col xs={24} data-testid="cronSelector">
              <ScheduleControl
                schedule={integration?.schedule}
                onChange={(value) => handleChange(value, 'schedule')}
                error={errors.schedule}
              />
            </Col>
            <Col xs={6} data-testid="timezoneSelector">
              <TimezoneSelector
                onChange={(value) => handleChange(value, 'timezone')}
                value={integration?.timezone}
                error={errors.timezone}
              />
            </Col>
            <Divider className={cn.divider}/>
            <Col xs={24}>
              <SectionHeader>
                Data types configuration
              </SectionHeader>
            </Col>
            <Col flex="auto">
              <Input
                label="Jobs report API endpoint"
                data-testid="Jobs report API endpoint_Input"
                data-sensitive="true"
                value={integration?.jobsConfig?.url}
                name="url"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleConfigChange(HRISConfigName.JOBS_CONFIG, event)}
                addonAfter={
                  <Button
                    className={cn.testButton}
                    loading={loadingTest}
                    onClick={() => testFileEndpoint(HRISConfigName.JOBS_CONFIG)}
                    disabled={testDisabled || !integration?.jobsConfig?.url}
                    type={ButtonType.Secondary}
                    data-testid="testButton"
                  >
                    Test
                  </Button>
                }
              />
            </Col>
            <Col flex="auto">
              <Input
                label="Employees report API endpoint"
                data-testid="Employees report API endpoint_Input"
                data-sensitive="true"
                value={integration?.employeesConfig?.url}
                name="url"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleConfigChange(HRISConfigName.EMPLOYEES_CONFIG, event)}
                addonAfter={
                  <Button
                    className={cn.testButton}
                    loading={loadingTest}
                    onClick={() => testFileEndpoint(HRISConfigName.EMPLOYEES_CONFIG)}
                    disabled={testDisabled || !integration?.employeesConfig?.url}
                    type={ButtonType.Secondary}
                    data-testid="testButton"
                  >
                    Test
                  </Button>
                }
              />
            </Col>
            <Col flex="auto">
              <Input
                label="Ranges report API endpoint"
                data-testid="Ranges report API endpoint_Input"
                data-sensitive="true"
                value={integration?.rangesConfig?.url}
                name="url"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleConfigChange(HRISConfigName.RANGES_CONFIG, event)}
                addonAfter={
                  <Button
                    className={cn.testButton}
                    loading={loadingTest}
                    onClick={() => testFileEndpoint(HRISConfigName.RANGES_CONFIG)}
                    disabled={testDisabled || !integration?.rangesConfig?.url}
                    type={ButtonType.Secondary}
                    data-testid="testButton"
                  >
                    Test
                  </Button>
                }
              />
            </Col>
          </Row>
          <Row gutter={[12, 24]} justify="end" className={cn.buttons}>
            {integration?.id && (
              <Col>
                <ConfirmModal
                  content={(
                    <div>
                      Are you sure you want to deactivate Workday RaaS integration? All data file integrations will be deleted.
                    </div>
                  )}
                  onConfirm={handleDeactivate}
                >
                  <Button
                    type={ButtonType.Danger}
                    loading={deleteLoading}
                    data-testid="deactivateButton"
                  >
                    Deactivate integration
                  </Button>
                </ConfirmModal>
              </Col>
            )}
            <Col>
              <Button onClick={handleSave} loading={saveLoading} data-testid={`${!integration?.id ? 'activate' : 'update'}Button`}>
                {!integration?.id ? 'Activate integration' : 'Update'}
              </Button>
            </Col>
          </Row>
        </>
      )}
    </div>
  )
}
